import { shallowEqual, useDispatch } from 'react-redux';

import { useRentalManagerUserPropertyEditWebApiHandler_modularListingProgress } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { logError } from '@zg-rentals/log-error';

import PropertiesActions from '../../actions/PropertiesActions';
import { useSelector } from '../../selectors/useSelector';

const DEFAULT_ERROR_MESSAGE = 'Could not save progress. Try again later.';

const useModularListingProgress = () => {
  const dispatch = useDispatch();

  const { isFeed } = useSelector((state) => ({ isFeed: state?.properties?.currentProperty?.isFeed }), shallowEqual);

  const { mutate, mutateAsync, ...rest } = useRentalManagerUserPropertyEditWebApiHandler_modularListingProgress();

  const wrappedMutate = (params: { propertyId: string; step: string }, options?: Parameters<typeof mutate>[1]) => {
    if (isFeed) {
      return;
    }

    return mutate(
      { getParams: { listingAlias: params.propertyId, step: params.step } },
      {
        ...options,
        onSuccess: (...args) => {
          dispatch(PropertiesActions.updateCurrentProperty({ modularListingProgress: args[0] }));
          options?.onSuccess?.(...args);
        },
        onError: (error, ...restArgs) => {
          const enrichedError = Object.assign({}, error, {
            message: (error as Error)?.message || DEFAULT_ERROR_MESSAGE,
          });

          logError({
            error: enrichedError,
            context: 'useModularListingProgress',
          });

          options?.onError?.(enrichedError, ...restArgs);
        },
      },
    );
  };

  const wrappedMutateAsync = async (
    params: { propertyId: string; step: string },
    options?: Parameters<typeof mutateAsync>[1],
  ) => {
    if (isFeed) {
      return;
    }

    try {
      const result = await mutateAsync({ getParams: { listingAlias: params.propertyId, step: params.step } }, options);

      dispatch(PropertiesActions.updateCurrentProperty({ modularListingProgress: result }));

      return result;
    } catch (error) {
      const enrichedError = Object.assign({}, error, {
        message: error?.message || DEFAULT_ERROR_MESSAGE,
      });

      logError({
        error: enrichedError,
        context: 'useModularListingProgress',
      });

      throw enrichedError;
    }
  };

  return { ...rest, mutate: wrappedMutate, mutateAsync: wrappedMutateAsync };
};

export { useModularListingProgress };
